import React from 'react';

export interface UploadedFile {
  id?: string | number;
  file_name: string;
  description: string;
  file_url?: string;
  file_size?: number;
  uploaded_at?: string;
  original_name?: string;
  document_type?: string;
  file_object?: File;
  isEditing?: boolean;
  // ✅ Support for buffered files and immediate-upload flow
  url?: string; 
  file_id?: string | number;
  alreadyUploaded?: boolean;
  isTemp?: boolean;
  _fileObject?: File;
  data?: any;
}

interface EditableFileCardProps {
  file: UploadedFile;
  index: number;
  onSave: (index: number, updatedData: UploadedFile) => void;
  onCancel: (index: number) => void;
  onEdit: (index: number) => void;
}

const API_URL = process.env.REACT_APP_API_URL;

export const EditableFileCard: React.FC<EditableFileCardProps> = ({ 
  file, 
  index, 
  onSave, 
  onCancel, 
  onEdit 
}) => {
  const [editForm, setEditForm] = React.useState({
    file_name: file.file_name,
    description: file.description || '',
    document_type: file.document_type || 'quote'
  });

  const handleEditChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const updatedFile = {
      ...file,
      file_name: editForm.file_name,
      description: editForm.description,
      document_type: editForm.document_type,
      isEditing: false
    };
    onSave(index, updatedFile);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formattedDate = file.uploaded_at 
    ? new Date(file.uploaded_at).toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : 'Recently';

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-semibold text-cyan-600">File</h4>
          <div className="flex space-x-2">
            <span className="bg-cyan-100 text-cyan-800 text-xs px-2 py-1 rounded-full">File</span>
            {!file.isEditing && (
              <button
                onClick={() => onEdit(index)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-2 py-1 rounded"
              >
                Edit
              </button>
            )}
          </div>
        </div>
        
        {file.isEditing ? (
          <div className="space-y-3">
            <div>
              <label className="block text-gray-600 text-sm mb-1">File Name</label>
              <input
                type="text"
                value={editForm.file_name}
                onChange={(e) => handleEditChange('file_name', e.target.value)}
                className="w-full p-2 border rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => handleEditChange('description', e.target.value)}
                className="w-full p-2 border rounded text-sm h-16"
                placeholder="File description..."
              />
            </div>
            <div>
              <label className="block text-gray-600 text-sm mb-1">Document Type</label>
              <select
                value={editForm.document_type}
                onChange={(e) => handleEditChange('document_type', e.target.value)}
                className="w-full p-2 border rounded text-sm"
              >
                <option value="quote">Quote</option>
                <option value="policy">Policy</option>
                <option value="claim">Claim</option>
                <option value="identification">Identification</option>
                <option value="proof_of_address">Proof of Address</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex space-x-2 pt-2">
              <button
                onClick={handleSave}
                className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1 rounded"
              >
                Save
              </button>
              <button
                onClick={() => onCancel(index)}
                className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-3 py-1 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">File Name:</span>
              <span className="font-medium text-right max-w-[150px] truncate" title={file.file_name}>
                {file.file_name}
              </span>
            </div>

            {file.original_name && file.original_name !== file.file_name && (
              <div className="flex justify-between">
                <span className="text-gray-600">Original Name:</span>
                <span className="font-medium text-gray-500 text-right max-w-[150px] truncate" title={file.original_name}>
                  {file.original_name}
                </span>
              </div>
            )}

            {file.file_size && (
              <div className="flex justify-between">
                <span className="text-gray-600">File Size:</span>
                <span className="font-medium">{formatFileSize(file.file_size)}</span>
              </div>
            )}

            {file.description && (
              <div className="flex flex-col">
                <span className="text-gray-600 mb-1">Description:</span>
                <span className="font-medium text-gray-800 text-xs bg-gray-50 p-2 rounded border">
                  {file.description}
                </span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-gray-600">Uploaded:</span>
              <span className="font-medium text-gray-500 text-xs">{formattedDate}</span>
            </div>

            {file.document_type && (
              <div className="flex justify-between">
                <span className="text-gray-600">Document Type:</span>
                <span className="font-medium text-blue-600 text-xs">{file.document_type}</span>
              </div>
            )}

            {typeof file.id === 'string' && file.id.startsWith('temp-') && (
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-blue-500 text-xs">
                  {file.isTemp ? 'Ready to upload' : 'Uploading...'}
                </span>
              </div>
            )}

            {/* ✅ Show preview for buffered files using local blob URL */}
            {file.url && !file.file_url && (
              <div className="pt-2">
                <a 
                  href={file.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  Preview File
                </a>
              </div>
            )}

            {file.file_url && !(typeof file.id === 'string' && file.id.startsWith('temp-')) && (
              <div className="pt-2">
                <a 
                  href={`${API_URL}${file.file_url}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download File
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditableFileCard;